import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import Moment from "react-moment";

/* ------------------------------------------------------------------ */
/*  PRINT / SCREEN CSS — injected into the DOM via <style> tag below   */
/* ------------------------------------------------------------------ */
const PRINT_STYLES = `
  .slip-page * { box-sizing: border-box; }

  .slip-page { 
    color: #000;
    max-width: 850px;
    margin: auto;
    background: #fff;
    padding: 8px 10px 20px 10px;
  }

  .slip-page ul { list-style: none; padding: 0; margin: 0; }
  .slip-page p,
  .slip-page h1,
  .slip-page h2,
  .slip-page h3,
  .slip-page h4 { margin: 0; padding: 0; }
  .name{
   text-transform:capitalize
  }

  /* ---------- Header ---------- */
  .slip-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 15px 12px 15px;
    border-bottom: 1px solid #000;
  }

  .slip-header-logo {
    width: 90px;
    height: 90px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .slip-header-center {
    text-align: center;
    flex: 1;
    padding: 0 15px;
  }

  .slip-college-name {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .slip-naac { font-size: 15px; margin-bottom: 4px; }
  .slip-academic-year { font-size: 15px; margin-bottom: 4px; }
  .slip-form-no { font-size: 15px; }

  /* ---------- Section titles ---------- */
  .slip-section-title {
    text-align: center;
    font-size: 17px;
    font-weight: 700;
    padding: 14px 0 10px 0;
    border-bottom: 1px solid #000;
    margin-bottom: 22px;
  }

  /* ---------- Two-column info grid ---------- */
  .slip-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 40px;
    row-gap: 20px;
    padding: 0 30px 28px 30px;
  }

  .slip-info-item {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    font-size: 15px;
  }

  .slip-info-label {
    font-weight: 700;
    min-width: 150px;
    flex-shrink: 0;
  }

  .slip-info-value { font-weight: 400; }

  /* ---------- Payment section ---------- */
  .slip-payment-title {
    text-align: center;
    font-size: 17px;
    font-weight: 700;
    padding: 14px 0 10px 0;
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    margin-bottom: 22px;
  }

  .slip-payment-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 40px;
    row-gap: 20px;
    padding: 0 30px 30px 30px;
  }

  .slip-payment-item {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    font-size: 15px;
  }

  .slip-payment-label {
    font-weight: 700;
    min-width: 170px;
    flex-shrink: 0;
  }

  .slip-payment-value { font-weight: 400; }

  /* ---------- Print-only rules ---------- */
  @media print {
    body { padding: 0; margin: 0; }
    .slip-page { max-width: 100%; padding: 0; }
    .no-print { display: none !important; }
  }
`;

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */
const OnlineFeeChallan = () => {
  const { id } = useParams();
  const contentRef = useRef(null);

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ---------- Fetch transaction from API ---------- */
  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const controller = new AbortController();

    const fetchTransaction = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `https://campus.acetians.in/api/v2/transaction/${id}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = await res.json();
        if (isMounted) setApiData(json);
      } catch (err) {
        if (err.name !== "AbortError" && isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTransaction();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id]);

  /* ---------- Derived values ---------- */
  const transaction = apiData?.transaction;
  const student = apiData?.student_details;
  const campus = apiData?.campus_details?.[0];
  const challan = apiData?.challan_details;

  const productParts = useMemo(
    () => (transaction?.productinfo || "").split("-"),
    [transaction?.productinfo]
  );
  const courseCode = productParts[0] || "";
  const yearCode = productParts[1] || "";

  const courseYear =
    yearCode === "1"
      ? "First Year"
      : yearCode === "2"
      ? "Second Year"
      : "Third Year";

  const academicYear = useMemo(() => {
    const dateStr = transaction?.addedon;
    if (dateStr) {
      const year = Number(dateStr.split(" ")[0]?.split("-")[0]);
      if (year) return `${year}-${String(year + 1).slice(-2)}`;
    }
    return challan?.session || "";
  }, [transaction?.addedon, challan?.session]);

  const [datePart, timePart] = useMemo(() => {
    const raw = transaction?.addedon || "";
    const [d = "", t = ""] = raw.split(" ");
    return [d, t];
  }, [transaction?.addedon]);

  /* ---------- Print handler ---------- */
  const handlePrint = useCallback(() => {
    const printContent = contentRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Admission Fee Slip</title>
          <style>${PRINT_STYLES}</style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();

    // Give images/styles a moment to settle before printing
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 300);
  }, []);

  /* ---------- Guards ---------- */
  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;
  if (error)
    return <div style={{ padding: 20, color: "red" }}>Error: {error}</div>;
  if (!apiData || !apiData.has_transaction)
    return <div style={{ padding: 20 }}>Transaction not found.</div>;

  const collegeLogo =
    campus?.college_logo ||
    "https://campus.acetians.in/custom/images/1722342842lalitpurlogo.png";
  const affiliationLogo =
    campus?.affiliation_logo ||
    "https://images.seeklogo.com/logo-png/21/1/uttar-pradesh-government-logo-png_seeklogo-217343.png";

  const studentName = student?.name || transaction?.firstName || "";
  const fatherName = student?.father_name || student?.fatherName || "";
  const dob = student?.dob || student?.date_of_birth || "";
  const category = student?.category || "";
  const gender = student?.gender || "";

  return (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "auto" }}>
      {/*  Inject styles into the on-screen DOM so the preview looks correct */}
      <style>{PRINT_STYLES}</style>

      <button
        className="no-print"
        onClick={handlePrint}
        style={{
          marginBottom: 16,
          padding: "8px 18px",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Print
      </button>

      <div ref={contentRef} className="slip-page">
        {/* ---------- Header ---------- */}
        <div className="slip-header">
          <img
            src={collegeLogo}
            alt="College Logo"
            className="slip-header-logo"
          />
          <div className="slip-header-center">
            <div className="slip-college-name">
              {campus?.college_name ||
                "रघुवीर सिंह राजकीय महाविद्यालय, ललितपुर,(उo.प्रo)"}
            </div>
            <div className="slip-naac">
              {campus?.college_description || "NAAC - B"}
            </div>
            <div className="slip-academic-year">
              Academic Year {academicYear}
            </div>
            <div className="slip-form-no">
              Form-No : {transaction?.id || "-"}
            </div>
          </div>
          <img
            src={affiliationLogo}
            alt="Affiliation Logo"
            className="slip-header-logo"
          />
        </div>

        {/* ---------- Admission fee slip title ---------- */}
        <div className="slip-section-title">Admission fee slip</div>

        {/* ---------- Student details ---------- */}
        <div className="slip-info-grid">
          <div className="slip-info-item">
            <span className="slip-info-label">Name:-</span>
            <span className="slip-info-value name">{studentName}</span>
          </div>
          <div className="slip-info-item">
            <span className="slip-info-label">Father Name:-</span>
            <span className="slip-info-value name">{fatherName}</span>
          </div>

          <div className="slip-info-item">
            <span className="slip-info-label">Course Name:-</span>
            <span className="slip-info-value">{courseCode}</span>
          </div>
          <div className="slip-info-item">
            <span className="slip-info-label">Course Year:-</span>
            <span className="slip-info-value">{courseYear}</span>
          </div>

          <div className="slip-info-item">
            <span className="slip-info-label">Caste Category:-</span>
            <span className="slip-info-value">{category}</span>
          </div>
          <div className="slip-info-item">
            <span className="slip-info-label">Gender:-</span>
            <span className="slip-info-value">{gender}</span>
          </div>

          <div className="slip-info-item">
            <span className="slip-info-label">Date Of Birth:-</span>
            <span className="slip-info-value">
              {dob ? <Moment format="DD/MM/YYYY">{dob}</Moment> : "-"}
            </span>
          </div>
        </div>

        {/* ---------- Online Payment Details ---------- */}
        <div className="slip-payment-title">Online Payment Details</div>

        <div className="slip-payment-grid">
          <div className="slip-payment-item">
            <span className="slip-payment-label">Date:-</span>
            <span className="slip-payment-value">
              {datePart ? (
                <Moment format="YYYY-MM-DD">{datePart}</Moment>
              ) : (
                "-"
              )}
            </span>
          </div>
          <div className="slip-payment-item">
            <span className="slip-payment-label">transaction number:-</span>
            <span className="slip-payment-value">
              {transaction?.txnid || "-"}
            </span>
          </div>

          <div className="slip-payment-item">
            <span className="slip-payment-label">Time:-</span>
            <span className="slip-payment-value">{timePart || "-"}</span>
          </div>
          <div className="slip-payment-item">
            <span className="slip-payment-label">Paid Amount:-</span>
            <span className="slip-payment-value">
              ₹{Number(transaction?.amount || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineFeeChallan;